import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  FireIcon,
  ChartBarIcon,
  CpuChipIcon,
  TrophyIcon,
  SparklesIcon,
  ArrowRightIcon,
  CheckCircleIcon,
  BoltIcon,
  PresentationChartLineIcon,
  TicketIcon,
} from '@heroicons/react/24/outline';

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.15 }
  }
};

const scaleIn = {
  hidden: { opacity: 0, scale: 0.9 },
  visible: { opacity: 1, scale: 1, transition: { duration: 0.5 } }
};

// Feature data
const features = [
  {
    icon: FireIcon,
    title: 'Player Streak Detection',
    description: 'AI-powered detection of hot streaks across all major sports. Know who\'s on fire before you place your bets.',
    gradient: 'from-orange-500 to-red-500',
    glow: 'shadow-orange-500/20'
  },
  {
    icon: SparklesIcon,
    title: 'Knewton AI Assistant',
    description: 'Your personal betting analyst. Get AI-powered insights, parlay suggestions, and real-time recommendations.',
    gradient: 'from-cyan-400 to-blue-500',
    glow: 'shadow-cyan-500/20'
  },
  {
    icon: PresentationChartLineIcon,
    title: 'Parlay Workshop',
    description: 'Build smarter parlays with our AI-assisted workshop. Select your vibe, configure your preferences, get winning combinations.',
    gradient: 'from-green-400 to-emerald-500',
    glow: 'shadow-green-500/20'
  },
  {
    icon: CpuChipIcon,
    title: 'Custom ML Models',
    description: 'Create and train your own prediction models. Fine-tune weights, select AI providers, and build your edge.',
    gradient: 'from-purple-500 to-pink-500',
    glow: 'shadow-purple-500/20'
  },
  {
    icon: ChartBarIcon,
    title: 'Betting Analytics',
    description: 'Track your performance with detailed analytics. ROI, win rates, streak analysis, and more.',
    gradient: 'from-blue-500 to-indigo-500',
    glow: 'shadow-blue-500/20'
  },
  {
    icon: TicketIcon,
    title: 'Bet Slip Tracking',
    description: 'Upload or manually enter your bet slips. Track every wager and analyze your betting patterns.',
    gradient: 'from-amber-500 to-orange-500',
    glow: 'shadow-amber-500/20'
  }
];

// Sports covered
const sports = [
  { name: 'NFL', emoji: '🏈' },
  { name: 'NBA', emoji: '🏀' },
  { name: 'NHL', emoji: '🏒' },
  { name: 'MLB', emoji: '⚾' },
  { name: 'NCAAF', emoji: '🎓' },
  { name: 'NCAAB', emoji: '🏀' }
];

// Stats (example numbers)
const stats = [
  { value: '6', label: 'Sports Covered' },
  { value: '10K+', label: 'Predictions Made' },
  { value: '71%', label: 'Win Rate' },
  { value: '24/7', label: 'AI Analysis' }
];

function FeatureCard({ feature, index }) {
  return (
    <motion.div
      variants={fadeInUp}
      className={`relative group p-6 rounded-2xl bg-secondary/50 backdrop-blur-sm border border-white/5 hover:border-white/10 transition-all duration-300 hover:shadow-xl ${feature.glow}`}
    >
      {/* Icon with gradient background */}
      <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${feature.gradient} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300`}>
        <feature.icon className="w-6 h-6 text-white" />
      </div>

      {/* Content */}
      <h3 className="text-xl font-semibold text-white mb-2">{feature.title}</h3>
      <p className="text-gray-400 leading-relaxed">{feature.description}</p>

      {/* Hover glow effect */}
      <div className={`absolute inset-0 rounded-2xl bg-gradient-to-br ${feature.gradient} opacity-0 group-hover:opacity-5 transition-opacity duration-300 -z-10`} />
    </motion.div>
  );
}

function Landing() {
  return (
    <div className="min-h-screen bg-primary overflow-hidden">
      {/* Background gradients */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-accent-blue/10 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-accent-green/10 rounded-full blur-3xl" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-purple-500/5 rounded-full blur-3xl" />
      </div>

      {/* Navigation */}
      <nav className="relative z-50 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-accent-blue to-accent-green flex items-center justify-center">
              <BoltIcon className="w-6 h-6 text-white" />
            </div>
            <span className="text-2xl font-bold text-gradient">UKnew</span>
          </Link>

          {/* Auth buttons */}
          <div className="flex items-center gap-4">
            <Link
              to="/login"
              className="px-4 py-2 text-gray-300 hover:text-white transition-colors font-medium"
            >
              Sign In
            </Link>
            <Link
              to="/signup"
              className="px-5 py-2.5 rounded-xl bg-gradient-to-r from-accent-blue to-accent-green text-white font-semibold hover:shadow-lg hover:shadow-accent-blue/25 transition-all duration-300"
            >
              Get Started
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative z-10 px-6 pt-16 pb-24">
        <div className="max-w-7xl mx-auto text-center">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
          >
            {/* Badge */}
            <motion.div variants={fadeInUp} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-accent-blue/10 border border-accent-blue/20 mb-8">
              <SparklesIcon className="w-4 h-4 text-accent-blue" />
              <span className="text-sm text-accent-blue font-medium">AI-Powered Sports Predictions</span>
            </motion.div>

            {/* Main headline */}
            <motion.h1
              variants={fadeInUp}
              className="text-5xl md:text-7xl font-bold text-white mb-6 leading-tight"
            >
              Smarter Bets Start{' '}
              <span className="text-gradient">Here</span>
            </motion.h1>

            {/* Subheadline */}
            <motion.p
              variants={fadeInUp}
              className="text-xl md:text-2xl text-gray-400 max-w-3xl mx-auto mb-10 leading-relaxed"
            >
              Advanced AI predictions, player streak detection, and custom ML models.
              Get the edge you need across NFL, NBA, NHL, MLB, and NCAA.
            </motion.p>

            {/* CTA Buttons */}
            <motion.div variants={fadeInUp} className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-16">
              <Link
                to="/signup"
                className="group flex items-center gap-2 px-8 py-4 rounded-xl bg-gradient-to-r from-accent-blue to-accent-green text-white font-semibold text-lg hover:shadow-xl hover:shadow-accent-blue/25 transition-all duration-300"
              >
                Start Winning Today
                <ArrowRightIcon className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link
                to="/login"
                className="flex items-center gap-2 px-8 py-4 rounded-xl bg-white/5 border border-white/10 text-white font-semibold text-lg hover:bg-white/10 transition-all duration-300"
              >
                Sign In
              </Link>
            </motion.div>

            {/* Sports Pills */}
            <motion.div variants={fadeInUp} className="flex flex-wrap items-center justify-center gap-3">
              {sports.map((sport) => (
                <div
                  key={sport.name}
                  className="flex items-center gap-2 px-4 py-2 rounded-full bg-secondary/50 border border-white/5"
                >
                  <span className="text-lg">{sport.emoji}</span>
                  <span className="text-sm font-medium text-gray-300">{sport.name}</span>
                </div>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="relative z-10 px-6 py-16 border-y border-white/5 bg-secondary/30">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="grid grid-cols-2 md:grid-cols-4 gap-8"
          >
            {stats.map((stat, index) => (
              <motion.div key={index} variants={scaleIn} className="text-center">
                <div className="text-4xl md:text-5xl font-bold text-gradient mb-2">{stat.value}</div>
                <div className="text-gray-400 font-medium">{stat.label}</div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section className="relative z-10 px-6 py-24">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            {/* Section header */}
            <motion.div variants={fadeInUp} className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
                Everything You Need to{' '}
                <span className="text-gradient">Win</span>
              </h2>
              <p className="text-xl text-gray-400 max-w-2xl mx-auto">
                Powerful tools designed to give you the competitive edge in sports betting.
              </p>
            </motion.div>

            {/* Features grid */}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {features.map((feature, index) => (
                <FeatureCard key={index} feature={feature} index={index} />
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="relative z-10 px-6 py-24 bg-secondary/30">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            <motion.div variants={fadeInUp} className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
                How It Works
              </h2>
              <p className="text-xl text-gray-400 max-w-2xl mx-auto">
                Get started in minutes and start making smarter predictions.
              </p>
            </motion.div>

            <div className="grid md:grid-cols-3 gap-8">
              {[
                { step: '01', title: 'Create Account', description: 'Sign up in seconds and get instant access to our prediction platform.' },
                { step: '02', title: 'Explore Insights', description: 'Browse predictions, player streaks, and AI-powered analytics across all sports.' },
                { step: '03', title: 'Make Smarter Bets', description: 'Use our tools to build parlays, track bets, and maximize your winnings.' }
              ].map((item, index) => (
                <motion.div
                  key={index}
                  variants={fadeInUp}
                  className="relative p-8 rounded-2xl bg-primary/50 border border-white/5"
                >
                  <div className="text-6xl font-bold text-accent-blue/20 mb-4">{item.step}</div>
                  <h3 className="text-xl font-semibold text-white mb-2">{item.title}</h3>
                  <p className="text-gray-400">{item.description}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative z-10 px-6 py-24">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
            className="relative p-12 rounded-3xl bg-gradient-to-br from-accent-blue/20 to-accent-green/20 border border-white/10 text-center overflow-hidden"
          >
            {/* Background glow */}
            <div className="absolute inset-0 bg-gradient-to-br from-accent-blue/10 to-accent-green/10 blur-3xl" />

            <div className="relative z-10">
              <TrophyIcon className="w-16 h-16 text-accent-blue mx-auto mb-6" />
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
                Ready to Start Winning?
              </h2>
              <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
                Join thousands of bettors using AI-powered predictions to make smarter decisions.
              </p>
              <Link
                to="/signup"
                className="inline-flex items-center gap-2 px-10 py-4 rounded-xl bg-gradient-to-r from-accent-blue to-accent-green text-white font-semibold text-lg hover:shadow-xl hover:shadow-accent-blue/25 transition-all duration-300"
              >
                Create Free Account
                <ArrowRightIcon className="w-5 h-5" />
              </Link>

              <div className="flex items-center justify-center gap-6 mt-8 text-sm text-gray-400">
                <span className="flex items-center gap-2">
                  <CheckCircleIcon className="w-5 h-5 text-accent-green" />
                  Free to start
                </span>
                <span className="flex items-center gap-2">
                  <CheckCircleIcon className="w-5 h-5 text-accent-green" />
                  No credit card required
                </span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative z-10 px-6 py-12 border-t border-white/5">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-accent-blue to-accent-green flex items-center justify-center">
              <BoltIcon className="w-4 h-4 text-white" />
            </div>
            <span className="text-lg font-bold text-gradient">UKnew</span>
          </div>
          <p className="text-gray-500 text-sm">
            &copy; {new Date().getFullYear()} UKnew. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}

export default Landing;
